import * as React from "react";

const COOKIE_CONSENT_KEY = "vendr-cookie-consent";
const COOKIE_PREFERENCES_KEY = "vendr-cookie-preferences";

export type CookiePreferences = {
  necessary: boolean; // Always true, required for site functionality
  analytics: boolean; // Segment, 6sense
  marketing: boolean; // Mutiny, advertising
  functional: boolean; // Intercom, enhanced features
};

export type ConsentState = "pending" | "accepted" | "declined" | "custom";

const defaultPreferences: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
};

const allAcceptedPreferences: CookiePreferences = {
  necessary: true,
  analytics: true,
  marketing: true,
  functional: true,
};

/**
 * Read the consent state from localStorage
 */
export const getConsentState = (): ConsentState | null => {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!stored) return null;

  const validStates: ConsentState[] = [
    "pending",
    "accepted",
    "declined",
    "custom",
  ];
  if (validStates.includes(stored as ConsentState)) {
    return stored as ConsentState;
  }
  return null;
};

/**
 * Read the cookie preferences from localStorage
 */
export const getStoredPreferences = (): CookiePreferences => {
  if (typeof window === "undefined") return defaultPreferences;
  const stored = localStorage.getItem(COOKIE_PREFERENCES_KEY);
  if (!stored) return defaultPreferences;
  try {
    const parsed = JSON.parse(stored);
    // Validate structure
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof parsed.necessary === "boolean" &&
      typeof parsed.analytics === "boolean" &&
      typeof parsed.marketing === "boolean" &&
      typeof parsed.functional === "boolean"
    ) {
      return parsed as CookiePreferences;
    }
    return defaultPreferences;
  } catch {
    return defaultPreferences;
  }
};

/**
 * Save consent state and preferences to localStorage
 */
export const saveConsent = (
  state: ConsentState,
  preferences: CookiePreferences,
): void => {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(COOKIE_CONSENT_KEY, state);
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(preferences));

    // Dispatch custom event to notify other parts of the app
    const event = new CustomEvent("vendrConsentUpdate", {
      detail: { state, preferences },
    });
    window.dispatchEvent(event);
  } catch (error) {
    console.error("Failed to save cookie consent:", error);
    // Could fall back to cookie storage or session storage
  }
};

export type CookieConsentContextProps = {
  /** Current consent state */
  consentState: ConsentState | null;
  /** Current preferences */
  preferences: CookiePreferences;
  /** Whether user has made any consent decision */
  hasConsented: boolean;
  /** Quick check: can we use analytics cookies? */
  canUseAnalytics: boolean;
  /** Quick check: can we use marketing cookies? */
  canUseMarketing: boolean;
  /** Quick check: can we use functional cookies? */
  canUseFunctional: boolean;
  /** Accept all cookies */
  acceptAll: () => void;
  /** Decline all optional cookies */
  declineAll: () => void;
  /** Save custom preferences */
  savePreferences: (preferences: CookiePreferences) => void;
  /** Show the consent banner again (for settings pages) */
  showBanner: () => void;
  /** Internal: banner visibility state */
  _showBanner?: boolean;
};

const defaultContextValue: CookieConsentContextProps = {
  consentState: null,
  preferences: defaultPreferences,
  hasConsented: false,
  canUseAnalytics: false,
  canUseMarketing: false,
  canUseFunctional: false,
  acceptAll: () => {},
  declineAll: () => {},
  savePreferences: () => {},
  showBanner: () => {},
};

export const CookieConsentContext =
  React.createContext<CookieConsentContextProps>(defaultContextValue);

/**
 * Hook to use cookie consent context
 */
export const useCookieConsent = () => React.useContext(CookieConsentContext);

/**
 * Provider component for cookie consent
 */
export const CookieConsentProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Use lazy initializers to read from localStorage synchronously before first render
  // This prevents hydration mismatches and UI flashing
  const [consentState, setConsentState] = React.useState<ConsentState | null>(
    () => getConsentState(),
  );
  const [preferences, setPreferences] = React.useState<CookiePreferences>(() =>
    getStoredPreferences(),
  );
  // Initialize banner as hidden to avoid hydration mismatch flash
  // We'll update it after mount based on actual consent state
  const [showBannerState, setShowBannerState] = React.useState(false);

  // Determine banner visibility after hydration to avoid SSR mismatch
  React.useEffect(() => {
    // Show banner only if no consent decision has been made
    setShowBannerState(getConsentState() === null);
  }, []);

  const acceptAll = React.useCallback(() => {
    setPreferences(allAcceptedPreferences);
    setConsentState("accepted");
    saveConsent("accepted", allAcceptedPreferences);
    setShowBannerState(false);
  }, []);

  const declineAll = React.useCallback(() => {
    setPreferences(defaultPreferences);
    setConsentState("declined");
    saveConsent("declined", defaultPreferences);
    setShowBannerState(false);
  }, []);

  const saveCustomPreferences = React.useCallback(
    (newPreferences: CookiePreferences) => {
      // Ensure necessary is always true
      const finalPreferences = { ...newPreferences, necessary: true };
      setPreferences(finalPreferences);
      setConsentState("custom");
      saveConsent("custom", finalPreferences);
      setShowBannerState(false);
    },
    [],
  );

  const showBanner = React.useCallback(() => {
    setShowBannerState(true);
  }, []);

  const value = React.useMemo<CookieConsentContextProps>(
    () => ({
      consentState,
      preferences,
      hasConsented: consentState !== null,
      canUseAnalytics: preferences.analytics,
      canUseMarketing: preferences.marketing,
      canUseFunctional: preferences.functional,
      acceptAll,
      declineAll,
      savePreferences: saveCustomPreferences,
      showBanner,
      // Internal state for banner visibility
      _showBanner: showBannerState,
    }),
    [
      consentState,
      preferences,
      showBannerState,
      acceptAll,
      declineAll,
      saveCustomPreferences,
      showBanner,
    ],
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

// Export constants for use in components
export { defaultPreferences, allAcceptedPreferences };
